import React from 'react';
import articleSlugs from './article-slugs.json';
import { LanguageCode, SUPPORTED_LANGUAGES, languageFromBrowser, languageFromPath, stripLanguage } from './languages';
import { latestModifiedDate } from '../util/seoMetadata';
export const PUBLIC_TRANSLATION_DATE = '2026-11-02';
export const PUBLIC_STATIC_PATHS = ['/', '/gambit', '/custom-books', '/faq', '/blog', '/stories', '/narrators', '/musicals', '/styles', '/languages', '/morals', '/videos', '/videos/stories', '/videos/narrators', '/payment', '/ai-story-generator', '/create-your-own-bedtime-stories', '/create-your-own-anime', '/bedtime-stories-for-kids', '/short-bedtime-stories', '/ai-story-generator-free', '/ai-story-writer', '/book-publisher', '/create-story-books-with-ai', '/classroom-stories-students-want-to-read', '/bible-story-generator'];
const DETAIL_PREFIXES = ['/blog/', '/faq/', '/styles/', '/narrators/', '/languages/', '/morals/', '/story-details/', '/musicals/', '/videos/'];
export const hasPublicTranslation = (path: string) => PUBLIC_STATIC_PATHS.includes(stripLanguage(path)) || DETAIL_PREFIXES.some(prefix => stripLanguage(path).startsWith(prefix));
const caseCache: Partial<Record<LanguageCode, Record<string, string>>> = {};
const templateCache: Partial<Record<LanguageCode, {
    key: string;
    expression: RegExp;
}[]>> = {};
const dictionaries: Partial<Record<LanguageCode, Record<string, string>>> = { en: {} };
export const currentPublicLanguage = () => languageFromPath(typeof window === 'undefined' ? '/' : window.location.pathname);
export const normalizeCopy = (text: string) => text.replace(/\s+/g, ' ').trim();
const loadedArticles: Partial<Record<LanguageCode, Set<string>>> = {};
export const articleForPath = (pathname: string): string | undefined => {
    const parts = stripLanguage(pathname).split('/').filter(Boolean);
    return parts.length === 2 && parts[0] === 'blog' && articleSlugs.includes(parts[1]) ? parts[1] : undefined;
};
export const isTranslationLoaded = (language: LanguageCode, article?: string) => language === 'en' || Boolean(dictionaries[language] && (!article || loadedArticles[language]?.has(article)));
export async function loadPublicTranslation(language: LanguageCode, article?: string) {
    if (language === 'en')
        return;
    if (!dictionaries[language]) {
        const loaded = await import(`./runtime-locales/${language}/common.json`);
        dictionaries[language] = loaded.default || loaded;
        delete caseCache[language];
        delete templateCache[language];
    }
    if (article && articleSlugs.includes(article) && !loadedArticles[language]?.has(article)) {
        const loaded = await import(`./runtime-locales/${language}/${article}.json`);
        Object.assign(dictionaries[language]!, loaded.default || loaded);
        (loadedArticles[language] ||= new Set()).add(article);
        delete caseCache[language];
        delete templateCache[language];
    }
}
/** Render-time lookup. Never changes customer text, IDs, assets, or backend payloads. */
export function pubText<T>(value: T, language: LanguageCode = currentPublicLanguage()): T {
    if (language === 'en' || (typeof value === 'string' && /^(Fable|fable|Go|Gambit|Pro|Premium|Ultra)$/.test(value.trim())))
        return value;
    if (Array.isArray(value))
        return value.map(item => pubText(item, language)) as T;
    if (typeof value !== 'string')
        return value;
    const translated = dictionaries[language]?.[normalizeCopy(value)];
    if (!translated) {
        const folded = caseCache[language] ||= Object.fromEntries(Object.entries(dictionaries[language] || {}).filter(([key]) => key.length < 150).map(([key, text]) => [key.toLocaleLowerCase('en'), text]));
        if (folded[normalizeCopy(value).toLocaleLowerCase('en')])
            return folded[normalizeCopy(value).toLocaleLowerCase('en')] as T;
        const numbered = value.match(/^(\d+[.)]\s+)(.+)$/);
        if (numbered && dictionaries[language]?.[normalizeCopy(numbered[2])])
            return (numbered[1] + pubText(numbered[2], language)) as T;
        const templates = templateCache[language] ||= Object.keys(dictionaries[language] || {}).filter(key => key.length < 500 && /__VALUE_\d+__/.test(key) && key.replace(/__VALUE_\d+__/g, '').replace(/[^A-Za-z]/g, '').length > 7).sort((a, b) => b.replace(/__VALUE_\d+__/g, '').length - a.replace(/__VALUE_\d+__/g, '').length).map(key => ({ key, expression: new RegExp('^' + key.split(/__VALUE_\d+__/).map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('(.+?)') + '$') }));
        for (const template of templates) {
            const match = normalizeCopy(value).match(template.expression);
            if (match && match.slice(1).every(part => part.length < value.length))
                return pubFormat(template.key, match.slice(1), language) as T;
        }
        if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && /[A-Za-z]{3}/.test(value)) {
            const debugWindow = window as unknown as {
                __FABLE_I18N_MISSING__?: Set<string>;
            };
            (debugWindow.__FABLE_I18N_MISSING__ ||= new Set()).add(normalizeCopy(value));
        }
        return value;
    }
    const leading = value.match(/^\s*/)?.[0] || '';
    const trailing = value.match(/\s*$/)?.[0] || '';
    return (leading + translated + trailing) as T;
}
/** Keep a selected language on public links; account, legal and external URLs retain their routes. */
export function pubPath<T>(value: T, language: LanguageCode = currentPublicLanguage()): T {
    if (typeof value !== 'string' || !value)
        return value;
    const absolute = value.startsWith('https://thefableapp.com');
    const relative = absolute ? value.slice('https://thefableapp.com'.length) || '/' : value;
    if (!relative.startsWith('/') || relative.startsWith('//'))
        return value;
    const suffixIndex = relative.search(/[?#]/);
    const pathname = suffixIndex < 0 ? relative : relative.slice(0, suffixIndex);
    const suffix = suffixIndex < 0 ? '' : relative.slice(suffixIndex);
    const base = stripLanguage(pathname);
    if (!hasPublicTranslation(base))
        return value;
    const path = language === 'en' ? base : `/${language}${base === '/' ? '' : base}`;
    return ((absolute ? 'https://thefableapp.com' : '') + path + suffix) as T;
}
/** Detect only on entry to unprefixed public pages. Explicit language URLs win. */
export function automaticPublicLanguagePath(path: string, languages: readonly string[]): string | null {
    const pathname = path.split(/[?#]/)[0];
    const prefix = pathname.split('/').filter(Boolean)[0];
    if (Object.prototype.hasOwnProperty.call(SUPPORTED_LANGUAGES, prefix) || !hasPublicTranslation(pathname))
        return null;
    const language = languageFromBrowser(languages);
    return language === 'en' ? null : pubPath(path, language);
}
const CONTENT_KEYS = new Set(['name', 'title', 'headline', 'text', 'description', 'question', 'answer', 'caption', 'articleSection', 'keywords', 'jobTitle', 'alternativeHeadline']);
const URL_KEYS = new Set(['url', '@id', 'item']);
export function translateStructuredData(json: string, language: LanguageCode, dateModified?: string): string {
    try {
        const visit = (value: any, key = '', originalMedia = false): any => {
            if (Array.isArray(value))
                return value.map(item => visit(item, key, originalMedia));
            if (value && typeof value === 'object') {
                const media = originalMedia || ['AudioObject', 'VideoObject', 'MusicRecording', 'MusicComposition', 'MediaObject', 'Book', 'Story', 'CreativeWork'].includes(value['@type']);
                const page = !media && ['WebPage', 'BlogPosting', 'Article', 'FAQPage'].includes(value['@type']);
                const fields = page && dateModified ? { ...value, dateModified: latestModifiedDate(value.dateModified, dateModified) } : value;
                return Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, visit(v, k, media)]));
            }
            if (key === 'inLanguage')
                return originalMedia ? value : language;
            if (typeof value === 'string' && CONTENT_KEYS.has(key))
                return pubText(value, language);
            if (typeof value === 'string' && URL_KEYS.has(key))
                return /#(organization|website|founder)$/.test(value) ? value : pubPath(value, language);
            return value;
        };
        return JSON.stringify(visit(JSON.parse(json)));
    }
    catch {
        return json;
    }
}
/** Preserve embedded markup and its identity when translating an inline sentence. */
export function pubRich(template: string, slots: React.ReactNode[], language = currentPublicLanguage()): React.ReactNode {
    return pubText(template, language).split(/(__SLOT_\d+__)/g).map((part, index) => {
        const match = part.match(/^__SLOT_(\d+)__$/);
        return match ? React.createElement(React.Fragment, { key: index }, slots[Number(match[1])]) : part;
    });
}
export function pubFormat(template: string, values: unknown[], language = currentPublicLanguage()): string {
    return pubText(template, language).replace(/__VALUE_(\d+)__/g, (_, index) => String(pubText(values[Number(index)], language) ?? ''));
}
/** Translate a sentence with inline emphasis as a whole. Only known React elements are cloned. */
export function pubInline(template: string, slots: React.ReactElement[], language = currentPublicLanguage()): React.ReactNode {
    const translated = pubText(template, language);
    const pieces = translated.split(/(<em\d+>[\s\S]*?<\/em\d+>|<br\s*\/>)/g);
    return pieces.map((piece, index) => {
        if (/^<br\s*\/>$/.test(piece))
            return React.createElement('br', { key: index });
        const match = piece.match(/^<em(\d+)>([\s\S]*?)<\/em\1>$/);
        if (!match)
            return piece;
        const element = slots[Number(match[1])];
        return element ? React.cloneElement(element as React.ReactElement<{
            children?: React.ReactNode;
        }>, { key: index }, match[2]) : match[2];
    });
}
